import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/useAuth'
import { ApiError } from '../lib/api'
import { rowWriteErrorMessage } from '../lib/portalAccess'
import { toast } from '../lib/toast'
import type { PortalAccessGrant, PortalRole } from '../lib/types'
import type { usePortalAccess } from './usePortalAccess'
import { useUnsavedGuard } from './useUnsavedGuard'

type PortalAccess = ReturnType<typeof usePortalAccess>

/** One staged role change: the row as last loaded, and the role picked. */
interface RoleChange {
  grant: PortalAccessGrant
  role: PortalRole
}

/** What a Save run did: whether any change saved, whether any was refused,
 *  and the first refusal's own message. */
interface SaveRun {
  saved: boolean
  failed: boolean
  failure: string | null
}

/** The order Save applies the changes in, so the server's last-Admin guard
 * never refuses a batch that ends with an Admin: every promotion first, then
 * the demotions, the signed-in Admin's own row last (a self-demotion takes
 * Settings away, so nothing after it could still be saved). */
export function saveOrder(changes: readonly RoleChange[], ownEmail: string | null): RoleChange[] {
  const rank = (c: RoleChange) => {
    if (c.role === 'admin') return 0
    return ownEmail !== null && c.grant.email.toLowerCase() === ownEmail ? 2 : 1
  }
  return [...changes].sort((a, b) => rank(a) - rank(b))
}

/** Settings › Access's page-level draft (ARCHITECTURE_DECISIONS.md §64): a
 * Permission select no longer saves on change. The picked roles are staged
 * here, keyed by grant id, and the page header's Save / Discard pair
 * (components/ui/PageSaveDiscard.tsx, as on Excess Volume and User Limits)
 * commits or drops them together; leaving the page with staged changes goes
 * through the shared unsaved-changes guard.
 *
 * Save sends one PATCH per changed row, one at a time in `saveOrder`. A row
 * that saves leaves the draft (the cached list already shows its new role);
 * a refused one stays staged so it can be retried or discarded, and the
 * first refusal is the notice under the table (`someNotSaved` when the
 * refusal has no message of its own: a 403 or 404). A 403 stops the run: the
 * caller lost Admin (or access) meanwhile and the rest would be refused too —
 * usePortalAccess already re-reads the session. The toast confirms a run in
 * which every change saved. The grants are refetched once, after the run.
 *
 * Discarding mid-save (Discard is inert then, but the unsaved-changes guard —
 * a navigation or Sign out — is not) stops the run before its next PATCH, so
 * no discarded change reaches the server; the one already in flight lands.
 *
 * A draft whose row was revoked, or whose role the server row now has, is
 * pruned when the list changes, so it cannot come back later.
 *
 * `notice` is the card's one inline message line, shared with Revoke: the
 * draft flow (picking a role, Discard, Save) clears only the notices it set
 * itself, never a Revoke error (`setNotice`). */
export function usePortalRoleDrafts(access: PortalAccess) {
  const { t } = useTranslation()
  const { userEmail } = useAuth()
  const ownEmail = userEmail?.toLowerCase() ?? null
  const [drafts, setDrafts] = useState<Record<string, PortalRole>>({})
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  // Bumped by every discard: a save run stops once it no longer matches.
  const generationRef = useRef(0)
  const [noticeEntry, setNoticeEntry] = useState<{ text: string; fromDrafts: boolean } | null>(null)
  const setNotice = useCallback((text: string | null) => {
    setNoticeEntry(text === null ? null : { text, fromDrafts: false })
  }, [])
  const clearDraftNotice = useCallback(() => {
    setNoticeEntry((current) => (current?.fromDrafts ? null : current))
  }, [])

  const rows = access.grants.data
  useEffect(() => {
    if (!rows) return
    setDrafts((current) => {
      const byId = new Map(rows.map((grant) => [grant.id, grant]))
      const next: Record<string, PortalRole> = {}
      let pruned = false
      for (const [id, role] of Object.entries(current)) {
        const row = byId.get(id)
        if (!row || row.role === role) pruned = true
        else next[id] = role
      }
      return pruned ? next : current
    })
  }, [rows])

  // Only rows still on the list count (until the prune above has run).
  const changes = useMemo<RoleChange[]>(
    () =>
      (rows ?? []).flatMap((grant) => {
        const role = drafts[grant.id]
        return role !== undefined && role !== grant.role ? [{ grant, role }] : []
      }),
    [rows, drafts]
  )
  const dirty = changes.length > 0

  const roleOf = useCallback((grant: PortalAccessGrant): PortalRole => drafts[grant.id] ?? grant.role, [drafts])

  const setRole = useCallback((grant: PortalAccessGrant, role: PortalRole) => {
    clearDraftNotice()
    setDrafts((current) => {
      const next = { ...current }
      if (role === grant.role) delete next[grant.id]
      else next[grant.id] = role
      return next
    })
  }, [clearDraftNotice])

  const reset = useCallback(() => {
    generationRef.current += 1
    setDrafts({})
    clearDraftNotice()
  }, [clearDraftNotice])
  useUnsavedGuard(dirty, reset)

  function discard() {
    if (!dirty || savingRef.current) return
    if (!window.confirm(t('settings.access.discardConfirm'))) return
    reset()
  }

  async function save() {
    if (!dirty || savingRef.current) return
    savingRef.current = true
    setSaving(true)
    clearDraftNotice()
    const generation = generationRef.current
    const discarded = () => generationRef.current !== generation
    let run: SaveRun
    try {
      run = await saveChanges(discarded)
    } finally {
      savingRef.current = false
      setSaving(false)
    }
    if (run.saved) access.refetchGrants()
    if (discarded()) return
    if (run.failed) {
      setNoticeEntry({ text: run.failure ?? t('settings.access.someNotSaved'), fromDrafts: true })
    } else {
      toast(t('settings.access.savedToast'))
    }
  }

  // Sends the staged changes one at a time, in `saveOrder`, until the run is
  // discarded or a 403 says the rest would be refused too.
  async function saveChanges(discarded: () => boolean): Promise<SaveRun> {
    const run: SaveRun = { saved: false, failed: false, failure: null }
    for (const change of saveOrder(changes, ownEmail)) {
      if (discarded()) break
      try {
        await access.changeRole.mutateAsync({ ...change, deferRefetch: true })
        run.saved = true
        if (discarded()) break
        setDrafts((current) => {
          const next = { ...current }
          delete next[change.grant.id]
          return next
        })
      } catch (err) {
        run.failed = true
        run.failure ??= rowWriteErrorMessage(err, t)
        if (err instanceof ApiError && err.status === 403) break
      }
    }
    return run
  }

  return { roleOf, setRole, dirty, saving, save, discard, notice: noticeEntry?.text ?? null, setNotice }
}

export type PortalRoleDrafts = ReturnType<typeof usePortalRoleDrafts>
